import type { IErrorInfo, IErrorDetails } from './errorInfo';
import { errorInfoSchema, addErrorDetails } from './errorInfo';

const reasonByCode: Record<number, string | undefined> = {
  1000: 'Normal Closure',
  1001: 'Going Away',
  1002: 'Protocol Error',
  1003: 'Unsupported Data',
  1005: 'No Status Received',
  1006: 'Abnormal Closure',
  1007: 'Invalid frame payload data',
  1008: 'Policy Violation',
  1009: 'Message too big',
  1010: 'Missing Extension',
  1011: 'Internal Error',
  1012: 'Service Restart',
  1013: 'Try Again Later',
  1014: 'Bad Gateway',
  1015: 'TLS Handshake',
};

function wsCodeToReason(code: number) {
  return reasonByCode[code] ?? '';
}

function isTruthy<T>(item: T | false | undefined | null | ''): item is T {
  return !!item;
}

export function shortErrorInfo(error: unknown): IErrorInfo {
  const { details, ...rest } = errorInfo(error);
  return rest;
}

function errorInfoFromObject(
  error: object,
  details?: IErrorDetails
): IErrorInfo {
  const anyError = error as {
    status?: string;
    code?: string | number;
    name?: string;
    message?: string;
    reason?: string;
    stack?: string;
  };

  let status = [
    typeof anyError.status === 'string' && anyError.status,
    typeof anyError.code === 'string' && anyError.code,
    typeof anyError.name === 'string' && anyError.name,
    'Error',
  ].filter(isTruthy)[0];

  let message = [
    typeof anyError.message === 'string' && anyError.message,
    typeof anyError.reason === 'string' && anyError.reason,
    'An unexpected error occurred',
  ].filter(isTruthy)[0];

  if (
    typeof anyError.code === 'number' &&
    anyError.code >= 1000 &&
    anyError.code <= 2000 &&
    typeof anyError.reason === 'string'
  ) {
    // socket error handling
    status = `WebSocket:${anyError.code}`;
    message = anyError.reason || wsCodeToReason(anyError.code);
  }

  const stack = typeof anyError.stack === 'string' && anyError.stack;

  return {
    status,
    message,
    details: {
      ...details,
      stack: stack || new Error().stack,
    },
  };
}

export function errorInfo(error: unknown, details?: IErrorDetails): IErrorInfo {
  const result = errorInfoSchema.validate(error) as {
    error?: Error;
    value?: IErrorInfo;
  };

  if (!result.error && result.value) {
    return addErrorDetails(result.value, details);
  } else if (typeof error === 'object' && error) {
    return errorInfoFromObject(error, details);
  } else if (typeof error !== 'object') {
    return {
      status: 'Error',
      message: String(error),
      details: {
        ...details,
        stack: new Error().stack,
      },
    };
  } else {
    return {
      status: 'Error',
      message:
        'Somebody forgot to pass down the error and we got no information, but it did happen!',
      details: {
        ...details,
        stack: new Error().stack,
      },
    };
  }
}
